import { motion } from "framer-motion";
import { Accessibility, KeyRound, MapPinned, Sparkles } from "lucide-react";

const FEATURES = [
    {
        num: "01",
        icon: Sparkles,
        color: "text-neon-cyan",
        glow: "group-hover:shadow-[0_0_40px_rgba(0,240,255,0.15)]",
        title: "Real-Time Cleanliness Ratings",
        body: "Community-rated conditions for every mapped restroom, refreshed as reports come in from the Strip.",
    },
    {
        num: "02",
        icon: KeyRound,
        color: "text-neon-gold",
        glow: "group-hover:shadow-[0_0_40px_rgba(255,215,0,0.12)]",
        title: "Keycard & Access Code Alerts",
        body: "Know before you walk — which casino floors need a room key, a players card, or a code from the bar.",
    },
    {
        num: "03",
        icon: Accessibility,
        color: "text-neon-magenta",
        glow: "group-hover:shadow-[0_0_40px_rgba(255,0,122,0.15)]",
        title: "ADA & Family Filters",
        body: "Filter instantly for accessible stalls, changing tables, and family restrooms across every property.",
    },
    {
        num: "04",
        icon: MapPinned,
        color: "text-neon-emerald",
        glow: "group-hover:shadow-[0_0_40px_rgba(0,255,102,0.12)]",
        title: "Offline Strip Map",
        body: "Dead zone in a casino basement? The full Strip map and restroom pins keep working without a signal.",
    },
];

export default function Features() {
    return (
        <section id="features" className="relative mx-auto max-w-7xl px-5 py-28 sm:px-8 sm:py-36" data-testid="features-section">
            <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                className="mb-16"
            >
                <p className="mb-4 font-mono text-xs uppercase tracking-[0.3em] text-neon-magenta">
                    01 — The App
                </p>
                <h2 className="max-w-2xl font-display text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">
                    BUILT FOR THE <span className="text-neon-cyan neon-cyan">3AM EMERGENCY</span> ON
                    LAS VEGAS BLVD
                </h2>
            </motion.div>

            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
                {FEATURES.map((f, i) => (
                    <motion.article
                        key={f.num}
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-100px" }}
                        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1], delay: i * 0.1 }}
                        className={`group glass-panel relative overflow-hidden rounded-2xl p-6 transition-shadow duration-500 ${f.glow}`}
                        data-testid={`feature-card-${f.num}`}
                    >
                        <div className="mb-8 flex items-start justify-between">
                            <f.icon className={`h-7 w-7 ${f.color}`} strokeWidth={1.8} />
                            <span className="font-mono text-xs text-slate-600">{f.num}</span>
                        </div>
                        <h3 className="mb-3 text-lg font-semibold text-white">{f.title}</h3>
                        <p className="text-sm leading-relaxed text-slate-400">{f.body}</p>
                        <div
                            className="pointer-events-none absolute -bottom-16 -right-16 h-40 w-40 rounded-full bg-neon-cyan/5 blur-3xl transition-opacity duration-500 opacity-0 group-hover:opacity-100"
                            aria-hidden="true"
                        />
                    </motion.article>
                ))}
            </div>
        </section>
    );
}
